/*
 * School teacher list helpers (TDI-616): a teacher is added once to their
 * school's list and chosen from a search for every other pupil.
 *
 * Pure functions only, shared by the "Add a teacher" modal and its tests.
 */

// The role is free text, required, and at most this long (server enforces too).
export const TEACHER_ROLE_MAX_LENGTH = 60;

// A search filters once it has this many letters (Tom, 31 October 2026).
export const TEACHER_SEARCH_MIN_LETTERS = 2;

const EMAIL_PATTERN = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export function teacherFullName(teacher) {
  if (!teacher) return '';
  return [teacher.firstName, teacher.lastName].filter(Boolean).join(' ');
}

/**
 * The school's teachers that match a search: two or more letters matching
 * the start of the first name, the last name, or any word in either (so
 * "Mc" finds "McAllister", "ann" finds "Mary Ann" and "obr" finds
 * "O'Brien"). Shorter searches filter nothing. Former teachers are dropped
 * unless asked for.
 *
 * @param {Array<{firstName?: string, lastName?: string, status?: string}>} teachers
 * @param {string} query
 * @param {{ includeFormer?: boolean }} [options]
 */
export function filterSchoolTeachers(teachers, query, { includeFormer = false } = {}) {
  const list = (teachers || []).filter(
    (t) => includeFormer || (t.status || 'active') === 'active',
  );
  const q = String(query || '').trim().toLowerCase();
  if (q.length < TEACHER_SEARCH_MIN_LETTERS) return list;
  const lettersOnly = (s) => s.replace(/[^\p{L}\p{N}]/gu, '');
  return list.filter((t) => {
    const names = [t.firstName, t.lastName].map((n) => String(n || '').toLowerCase());
    const words = names.join(' ').split(/[\s'’-]+/).filter(Boolean);
    const candidates = [names.join(' ').trim(), ...names, ...names.map(lettersOnly), ...words];
    return candidates.some((c) => c && c.startsWith(q));
  });
}

// The referral forms a teacher can be given (TDI-618, Tom's ruling T-3):
// Education history and the ADHD symptom scale only. Mirrors
// TEACHER_REFERRAL_FORMS in rms_functions/rms_harbour.py — keep in sync.
export const TEACHER_REFERRAL_FORMS = ['educationHistory', 'snapIV26'];

// "Send a reminder" at most once in this long (the design's 24-hour gap).
export const TEACHER_REMINDER_COOLDOWN_MS = 24 * 60 * 60 * 1000;

/**
 * The forms picker's groups (TDI-618): "Referral" then "SEND pathway", each
 * holding only the forms a teacher can have of those the record carries.
 * An empty group is left out.
 *
 * @param {{ referralFormKeys?: string[], senFormKeys?: string[] }} args - the
 *   record's own school forms per pathway (harbourFormKeysFor)
 * @returns {Array<{ pathway: 'referral'|'sen', label: string, formKeys: string[] }>}
 */
export function teacherFormGroups({ referralFormKeys = [], senFormKeys = [] } = {}) {
  const groups = [];
  const referral = referralFormKeys.filter((k) => TEACHER_REFERRAL_FORMS.includes(k));
  if (referral.length) groups.push({ pathway: 'referral', label: 'Referral', formKeys: referral });
  if (senFormKeys.length) groups.push({ pathway: 'sen', label: 'SEND pathway', formKeys: [...senFormKeys] });
  return groups;
}

const participantKey = (t) => (t && (t.participantId || t.id)) || null;

/**
 * Who holds each form among the OTHER teachers on the record: one person
 * holds a form at a time (TDI-618 criterion 3).
 *
 * @param {Array} teachers - teacher participants with `allocations`
 * @param {object} teacher - the teacher whose forms are being chosen
 * @returns {Record<string, object>} formKey -> the teacher holding it
 */
export function formHoldersExcept(teachers, teacher) {
  const self = participantKey(teacher);
  const holders = {};
  for (const t of teachers || []) {
    if (participantKey(t) === self) continue;
    for (const formKey of Object.keys(t.allocations || {})) {
      if ((t.allocations || {})[formKey]) holders[formKey] = t;
    }
  }
  return holders;
}

/**
 * What a save would change: forms given and forms taken back.
 * @param {string[]} original
 * @param {string[]} draft
 */
export function teacherFormChanges(original = [], draft = []) {
  const added = draft.filter((k) => !original.includes(k));
  const removed = original.filter((k) => !draft.includes(k));
  return { added, removed, changed: added.length > 0 || removed.length > 0 };
}

const isSchoolFormDone = (formProgress, formKey) => {
  const raw = (formProgress || {})[`${formKey}_school_user`] ?? (formProgress || {})[formKey];
  return Number.isFinite(Number(raw)) && Number(raw) >= 100;
};

const toDate = (value) => {
  if (!value) return null;
  const d = typeof value.toDate === 'function' ? value.toDate() : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

/** "3 November" */
export function formatDayMonth(value) {
  const d = toDate(value);
  return d ? d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long' }) : null;
}

/** "today at 10:24" / "3 November at 10:24" */
export function formatWhen(value, now = new Date()) {
  const d = toDate(value);
  if (!d) return null;
  const time = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  const sameDay = d.toDateString() === now.toDateString();
  return sameDay ? `today at ${time}` : `${formatDayMonth(d)} at ${time}`;
}

/**
 * The one state a teacher row shows, and its one action (TDI-618 criteria
 * 5 and 6). Nothing until the teacher holds a form.
 *
 * State: "Email sent {date}" until they sign in; "Signed in" once they
 * have; "{x} of {y} forms done" once any of their forms is done.
 * Action: "resend" (Send the email again) until they have set a password;
 * then "remind" (Send a reminder) while forms are unfinished, or
 * "reminded" (Reminder sent {time}) inside 24 hours of the last one.
 *
 * @param {object} teacher - participant: allocations, status, emailSentAt,
 *   lastReminderAt, teacherHasPassword (from the server overlay)
 * @param {object} formProgress - percent per instance, 100 meaning done
 * @param {Date} [now]
 * @returns {{ label: string|null, action: 'resend'|'remind'|'reminded'|null,
 *   reminderSentAt: string|null, done: number, total: number }}
 */
export function teacherRowState(teacher, formProgress, now = new Date()) {
  const keys = Object.keys((teacher && teacher.allocations) || {});
  const total = keys.length;
  const done = keys.filter((k) => isSchoolFormDone(formProgress, k)).length;
  if (!total) return { label: null, action: null, reminderSentAt: null, done, total };

  const hasPassword = teacher.teacherHasPassword === true;
  const signedIn = hasPassword || teacher.status === 'account_linked';
  let label = null;
  if (signedIn && done > 0) label = `${done} of ${total} forms done`;
  else if (signedIn) label = 'Signed in';
  else if (teacher.emailSentAt) label = `Email sent ${formatDayMonth(teacher.emailSentAt)}`;
  else if (teacher.status === 'invite_sent') label = 'Email sent';

  let action = null;
  let reminderSentAt = null;
  if (!hasPassword) {
    action = 'resend';
  } else if (done < total) {
    const last = toDate(teacher.lastReminderAt);
    if (last && now.getTime() - last.getTime() < TEACHER_REMINDER_COOLDOWN_MS) {
      action = 'reminded';
      reminderSentAt = teacher.lastReminderAt;
    } else {
      action = 'remind';
    }
  }
  return { label, action, reminderSentAt, done, total };
}

/**
 * The toast after a leaver is marked: how many unfinished forms came back.
 * @param {number} n
 */
export function formsBackLine(n) {
  if (n === 1) return '1 unfinished form is back with you.';
  return `${n} unfinished forms are back with you.`;
}

/**
 * The new-teacher details form's errors, keyed by field, in the ticket's
 * wording. Empty object when the form can be sent.
 *
 * @param {{ firstName: string, lastName: string, email: string, jobRole: string }} form
 */
export function newTeacherErrors(form) {
  const errors = {};
  const email = String(form.email || '').trim();
  const role = String(form.jobRole || '').trim();
  if (!String(form.firstName || '').trim()) errors.firstName = 'Enter the teacher’s first name';
  if (!String(form.lastName || '').trim()) errors.lastName = 'Enter the teacher’s last name';
  if (!email) errors.email = 'Enter the teacher’s email address';
  else if (!EMAIL_PATTERN.test(email)) errors.email = 'Enter an email address in the correct format, like name@example.com';
  if (!role) errors.jobRole = 'Enter the teacher’s role';
  else if (role.length > TEACHER_ROLE_MAX_LENGTH) errors.jobRole = `The teacher’s role must be ${TEACHER_ROLE_MAX_LENGTH} characters or fewer`;
  return errors;
}
